import React, { useState, useEffect } from "react";
import { getAnalitics, getAnaliticsSum } from "../../api/apiServiceFetch.js";
import DataGrid, {
	Column,
	FilterRow,
	HeaderFilter,
	Paging,
	Toolbar,
	LoadPanel,
	Selection,
	Item as ToolbarItem,
} from "devextreme-react/data-grid";
import { Button as DxButton } from "devextreme-react/button";
import "devextreme/dist/css/dx.light.css";
import "./Analitics.scss";

// вспомогательная функция: превращаем Date/строку в 'YYYY-MM-DD'
const formatDate = (value) => {
	if (!value) return null;
	if (value instanceof Date) {
		return value.toISOString().slice(0, 10);
	}
	// если уже строка 'YYYY-MM-DD'
	return value;
};

const formatMoney = (value) => {
	const num = Number(value ?? 0);

	return new Intl.NumberFormat("ru-RU", {
		minimumFractionDigits: 0,
		maximumFractionDigits: 2,
	}).format(num);
};

const Analitics = () => {
	// режим отчёта: today | day | thisweek | lastweek | month | year
	const [mode, setMode] = useState("today");
	const [refDate, setRefDate] = useState(() => new Date());
	const [reportRows, setReportRows] = useState([]);
	const [loading, setLoading] = useState(false);
	const [analyticsSum, setAnalyticsSum] = useState(0);

	const buildRequest = () => ({
		mode,
		refDate: mode === "today" ? null : formatDate(refDate),
		toDate: null,
	});

	const fetchReport = async () => {
		setLoading(true);

		try {
			const req = buildRequest();

			const [data, sum] = await Promise.all([
				getAnalitics(req),
				getAnaliticsSum(req),
			]);

			setReportRows(data);
			setAnalyticsSum(Number(sum ?? 0));
		} catch (err) {
			console.error("Error fetching report data:", err);

			setReportRows([]);
			setAnalyticsSum(0);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		// первичная загрузка
		fetchReport();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// ---- Управление видимостью колонок по типу отчёта ----
	const isDayMode = mode === "today" || mode === "day";
	const isWeekMode = mode === "thisweek" || mode === "lastweek";
	const isMonthMode = mode === "month";
	const isYearMode = mode === "year";

	const showDay = isDayMode; // только в дневных отчётах
	const showWeek = isWeekMode; // только недельные
	const showMonth = isDayMode || isWeekMode || isMonthMode; // в день/неделю/месяц
	const showYear = true; // год всегда полезен для фильтрации/понимания

	return (
		<div style={{ width: "100%" }}>
			<h2>Аналитика заказов</h2>

			<div
				className="analitics-filters"
				style={{ marginBottom: 16, display: "flex", gap: 12, flexWrap: "wrap" }}
			>
				<label>
					Период:&nbsp;
					<select value={mode} onChange={(e) => setMode(e.target.value)}>
						<option value="today">Сегодня</option>
						<option value="day">Конкретный день</option>
						<option value="thisweek">Эта неделя</option>
						<option value="lastweek">Прошлая неделя</option>
						<option value="month">Месяц</option>
						<option value="year">Год</option>
					</select>
				</label>

				{mode !== "today" && (
					<label>
						Дата:&nbsp;
						<input
							type="date"
							value={formatDate(refDate) || ""}
							onChange={(e) => setRefDate(e.target.value)}
						/>
					</label>
				)}

				<DxButton
					text="Показать"
					icon="search"
					type="default"
					stylingMode="contained"
					onClick={fetchReport}
				/>
				<div className="analitics-sum">
					<span className="analitics-sum__label">Сумарно:</span>
					<span className="analitics-sum__value">
						{loading ? "..." : `${formatMoney(analyticsSum)} ₽`}
					</span>
				</div>
			</div>

			<div>
				<DataGrid
					dataSource={reportRows}
					showBorders={true}
					allowColumnResizing={true}
					columnAutoWidth={true}
				>
					<LoadPanel enabled={loading} />
					<Paging defaultPageSize={20} />
					<FilterRow visible={true} />
					<HeaderFilter visible={true} />
					<Selection mode="single" />

					<Toolbar>
						<ToolbarItem location="after">
							<DxButton hint="Обновить" icon="refresh" onClick={fetchReport} />
						</ToolbarItem>
					</Toolbar>

					{/* ВАЖНО: названия полей должны совпадать с JSON с бэка.
              Если у тебя DTO с PascalCase (GameName/ItemName/...), поменяй dataField на них. */}
					<Column
						dataField="year"
						caption="Год"
						width={80}
						dataType="number"
						visible={showYear}
					/>
					<Column
						dataField="month"
						caption="Месяц"
						width={80}
						dataType="number"
						visible={showMonth}
					/>
					<Column
						dataField="week"
						caption="Неделя"
						width={90}
						dataType="number"
						visible={showWeek}
					/>
					<Column
						dataField="day"
						caption="Дата"
						dataType="date"
						width={120}
						visible={showDay}
					/>

					<Column dataField="gameName" caption="Игра" minWidth={150} />
					<Column dataField="itemName" caption="Товар" minWidth={200} />

					<Column
						dataField="totalAmount"
						caption="Сумма, RUB"
						dataType="number"
						format="#,##0"
						width={130}
					/>
					<Column
						dataField="itemsCount"
						caption="Кол-во покупок"
						dataType="number"
						width={140}
					/>
				</DataGrid>
			</div>
		</div>
	);
};

export default Analitics;
